import React from "react";
import { useTranslation } from "react-i18next";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import {
  TableRow,
  CustomCard,
  HeaderColumn,
  BootstrapTable,
  TableDetailCell,
  LastHeaderColumn,
  FirstHeaderColumn,
  WebsiteStatusBadge
} from "../common-styling";
import NoResultsRow from "../no-results-table-row";
import { EmptyState } from "./split-test.styles";
import {
  STATUS_BADGE,
  describeValue,
  elementTypeOf,
  formatDays,
  formatNumber,
  formatPValue,
  formatSignedPercent
} from "./split-test.helpers";
import { generateDate } from "../../services/common-service";
import { hasAccess } from "../../services/access";
import { ACCESS_CONFIG, SPLIT_TEST_STATUS } from "../strings";

const HistoryTable = ({ history, elements, busy, onRevert, onRollout }) => {
  const { t } = useTranslation();
  const canRevert = hasAccess(ACCESS_CONFIG.ONLY_SUPER_ADMIN);
  // a won test whose automatic roll-out failed can be rolled out again here
  // (review finding Hamza 07.10.2026: the endpoint existed, the button not)
  const canRollout = hasAccess(ACCESS_CONFIG.ADMIN_TIER);
  const elementLabel = (key) =>
    elements?.find((e) => e.key === key)?.label || key;
  const colSpan = 8;

  if (!history?.length) {
    return (
      <CustomCard className="p-3 rounded-0">
        <EmptyState>
          <div className="title">{t("splitTests.history.emptyTitle")}</div>
          <div>{t("splitTests.history.emptyText")}</div>
        </EmptyState>
      </CustomCard>
    );
  }

  return (
    <CustomCard className="p-3 rounded-0">
      <BootstrapTable responsive>
        <thead>
          <tr>
            <FirstHeaderColumn>
              {t("splitTests.fields.element")}
            </FirstHeaderColumn>
            <HeaderColumn>{t("splitTests.history.texts")}</HeaderColumn>
            <HeaderColumn className="text-center">
              {t("splitTests.history.result")}
            </HeaderColumn>
            <HeaderColumn className="text-center">
              {t("splitTests.stats.lift")}
            </HeaderColumn>
            <HeaderColumn className="text-center">
              {t("splitTests.stats.pValue")}
            </HeaderColumn>
            <HeaderColumn className="text-center">
              {t("splitTests.history.runtime")}
            </HeaderColumn>
            <HeaderColumn className="text-center">
              {t("splitTests.history.rollout")}
            </HeaderColumn>
            <LastHeaderColumn className="text-end">
              {t("common.action")}
            </LastHeaderColumn>
          </tr>
        </thead>
        <tbody>
          {history.length ? (
            history.map((item) => {
              const s = item.stats || {};
              const exposures = (s.A?.exposures || 0) + (s.B?.exposures || 0);
              const canRevertRow =
                canRevert &&
                item.status === SPLIT_TEST_STATUS.WON &&
                item.rolledOutAt;
              const canRolloutRow =
                canRollout &&
                item.status === SPLIT_TEST_STATUS.WON &&
                !item.rolledOutAt;
              return (
                <TableRow key={item._id}>
                  <TableDetailCell className="py-3 fw-semibold">
                    {elementLabel(item.element)}
                    <div className="small text-muted fw-normal">
                      {generateDate(item.startedAt)} –{" "}
                      {generateDate(item.endedAt)}
                    </div>
                  </TableDetailCell>
                  <TableDetailCell className="py-3">
                    <div className="small">
                      <span className="text-muted">A:</span>{" "}
                      {describeValue(
                        elementTypeOf(elements, item.element),
                        item.controlValue,
                        t
                      )}
                    </div>
                    <div className="small">
                      <span className="text-muted">B:</span>{" "}
                      {describeValue(
                        elementTypeOf(elements, item.element),
                        item.variantValue,
                        t
                      )}
                    </div>
                  </TableDetailCell>
                  <TableDetailCell className="py-3 text-center">
                    <WebsiteStatusBadge $stage={STATUS_BADGE[item.status]}>
                      {t(`splitTests.status.${item.status}`)}
                    </WebsiteStatusBadge>
                  </TableDetailCell>
                  <TableDetailCell className="py-3 text-center">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          A {formatNumber(s.A?.conversions)}/
                          {formatNumber(s.A?.exposures)} · B{" "}
                          {formatNumber(s.B?.conversions)}/
                          {formatNumber(s.B?.exposures)}
                        </Tooltip>
                      }
                    >
                      <span
                        className={
                          s.lift > 0
                            ? "text-success fw-semibold"
                            : s.lift < 0
                              ? "text-danger"
                              : ""
                        }
                      >
                        {formatSignedPercent(s.lift)}
                      </span>
                    </OverlayTrigger>
                    <div className="small text-muted">
                      {formatNumber(exposures)}{" "}
                      {t("splitTests.history.visitors")}
                    </div>
                  </TableDetailCell>
                  <TableDetailCell className="py-3 text-center">
                    {formatPValue(s.pValue)}
                  </TableDetailCell>
                  <TableDetailCell className="py-3 text-center">
                    {formatDays(s.daysRunning)} {t("splitTests.history.days")}
                  </TableDetailCell>
                  <TableDetailCell className="py-3 text-center">
                    {item.rolledOutAt
                      ? t("splitTests.history.rolledOutTo", {
                          funnels: item.rollout?.funnelsUpdated ?? 0,
                          templates: item.rollout?.templatesUpdated ?? 0
                        })
                      : "–"}
                    {item.status === SPLIT_TEST_STATUS.WON &&
                      !item.rolledOutAt && (
                        <div className="small text-danger">
                          {t("splitTests.history.rolloutPending")}
                          {item.rollout?.error ? (
                            <div className="text-muted">
                              {item.rollout.error}
                            </div>
                          ) : null}
                        </div>
                      )}
                  </TableDetailCell>
                  <TableDetailCell className="py-3 text-end">
                    {canRolloutRow ? (
                      <Button
                        size="sm"
                        variant="outline-primary"
                        disabled={busy}
                        onClick={() => onRollout(item)}
                      >
                        {t("splitTests.actions.retryRollout")}
                      </Button>
                    ) : canRevertRow ? (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={busy}
                        onClick={() => onRevert(item)}
                      >
                        {t("splitTests.actions.revert")}
                      </Button>
                    ) : (
                      <span className="text-muted">–</span>
                    )}
                  </TableDetailCell>
                </TableRow>
              );
            })
          ) : (
            <NoResultsRow lengthOfColumns={colSpan} />
          )}
        </tbody>
      </BootstrapTable>
    </CustomCard>
  );
};

export default HistoryTable;
